import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { Card } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { applyBranding, logoUrl } from '@/lib/branding';
import type { PublicBranding } from '@/lib/types';

export function usePublicBranding() {
  return useQuery({
    queryKey: ['public', 'tenant'],
    queryFn: () => api<PublicBranding>('/public/tenant'),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Rahmen für Anmeldung, Passwort-Reset und Einladung – im Branding des Mandanten. */
export function AuthLayout({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  const { data: branding, isError } = usePublicBranding();

  useEffect(() => {
    if (branding) applyBranding(branding);
  }, [branding]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-gradient-to-b from-primary/8 via-background to-background px-4 py-10">
      <div className="mb-6 flex flex-col items-center gap-3 text-center">
        {branding?.hasLogo ? (
          <img src={logoUrl(branding.brandingVersion)} alt={branding.name} className="h-12 max-w-[220px] object-contain" />
        ) : (
          <div className="flex size-12 items-center justify-center rounded-xl bg-primary text-lg font-bold text-primary-foreground shadow-sm">
            {branding?.name.charAt(0) ?? 'S'}
          </div>
        )}
        <p className="text-sm font-medium text-muted-foreground">{branding?.name ?? 'Serviceportal'}</p>
      </div>
      <Card className="w-full max-w-sm p-6 shadow-sm">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        {isError ? (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            Für diese Adresse ist kein Mandant eingerichtet. Bitte wenden Sie sich an Ihren Administrator.
          </p>
        ) : (
          <div className="mt-5">{children}</div>
        )}
      </Card>
      <p className="mt-6 text-xs text-muted-foreground">Wolbeck Serviceportal · Open Source</p>
    </div>
  );
}
